import { createRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { Linkedin, Mail, User, Monitor, Heart, AlertTriangle, Shield, Cpu, Database, Server, HardDrive } from 'lucide-react';
import { Shell } from '../components/layout/Shell';
import { SupportCard } from '../components/ui/SupportCard';
import { Button } from '../components/ui/Button';
import { useTranslation } from '../i18n';
import { useAppMode } from '../context/AppModeContext';
import { DESKTOP_PRICE } from '../constants/external';
import { Route as rootRoute } from './__root';
import styles from './about.module.css';

export const Route = createRoute({
    getParentRoute: () => rootRoute,
    path: '/about',
    component: AboutPage,
});

function AboutPage() {
    const { t } = useTranslation();
    const { mode, aiTier } = useAppMode();

    return (
        <Shell>
            <div className={styles.page}>
                <div className={styles.bento}>
                    {/* Top-left: Hero + Developer merged */}
                    <div className={styles.heroDevCard}>
                        <div className={styles.heroStrip}>
                            <div className={styles.heroMonitor}>
                                <Monitor size={32} strokeWidth={1.25} />
                            </div>
                            <div className={styles.heroText}>
                                <div className={styles.heroName}>
                                    SiliconVault
                                    <span className={styles.heroVersion}>v{__APP_VERSION__}</span>
                                </div>
                                <div className={styles.heroTagline}>{t('settings.aboutLine2')}</div>
                            </div>
                        </div>
                        <p className={styles.heroDesc}>{t('about.appDescription')}</p>
                        <div className={styles.cardDivider} />
                        <div className={styles.devLabel}>{t('about.developerTitle')}</div>
                        <div className={styles.developerRow}>
                            <User size={14} strokeWidth={1.75} />
                            <span className={styles.developerName}>{t('about.developerName')}</span>
                        </div>
                        <div className={styles.developerRow}>
                            <Linkedin size={14} strokeWidth={1.75} />
                            <a href="https://www.linkedin.com/in/asafsaar/" target="_blank" rel="noopener noreferrer" className={styles.developerLink}>{t('about.developerLinkedIn')}</a>
                        </div>
                        <div className={styles.developerRow}>
                            <Mail size={14} strokeWidth={1.75} />
                            <a href="mailto:info@corgimind.com" className={styles.developerLink}>info@corgimind.com</a>
                        </div>
                        <div className={styles.developerBio}>
                            {t('about.developerBio')}
                        </div>
                    </div>

                    {/* License & Terms */}
                    <div className={styles.licenseCard}>
                        <div className={styles.licenseHeader}>
                            <h2 className={styles.licenseLabel}>{t('about.licenseTitle')}</h2>
                        </div>
                        <div className={styles.licenseBlock}>
                            <Heart size={15} strokeWidth={1.75} className={styles.licenseIcon} />
                            <div>
                                <p className={styles.licenseText}>{t('about.licenseBody')}</p>
                                <p className={styles.licenseText}>{t('about.communityBody')}</p>
                            </div>
                        </div>
                        <div className={styles.sectionDivider} />
                        <div className={styles.licenseBlock}>
                            <Shield size={15} strokeWidth={1.75} className={styles.sectionIcon} />
                            <div>
                                <h3 className={styles.sectionTitle}>{t('about.privacyTitle')}</h3>
                                <p className={styles.licenseText}>{t('about.privacyBody')}</p>
                            </div>
                        </div>
                        <div className={styles.sectionDivider} />
                        <div className={styles.licenseBlock}>
                            <Cpu size={15} strokeWidth={1.75} className={styles.sectionIcon} />
                            <div>
                                <h3 className={styles.sectionTitle}>{t('about.aiTitle')}</h3>
                                <p className={styles.licenseText}>{t('about.aiBody')}</p>
                            </div>
                        </div>
                        <div className={styles.sectionDivider} />
                        <div className={styles.licenseBlock}>
                            <Database size={15} strokeWidth={1.75} className={styles.sectionIcon} />
                            <div>
                                <h3 className={styles.sectionTitle}>{t('about.dataTitle')}</h3>
                                <p className={styles.licenseText}>{t('about.dataBody')}</p>
                            </div>
                        </div>
                        <div className={styles.sectionDivider} />
                        <div className={styles.licenseBlock}>
                            <Server size={15} strokeWidth={1.75} className={styles.sectionIcon} />
                            <div>
                                <h3 className={styles.sectionTitle}>{t('about.techTitle')}</h3>
                                <p className={styles.licenseText}>{t('about.techBody')}</p>
                            </div>
                        </div>
                    </div>

                    {/* Bottom-right: Disclaimer */}
                    <div className={styles.disclaimerCard}>
                        <div className={styles.licenseHeader}>
                            <h2 className={styles.licenseLabel}>{t('about.disclaimerTitle')}</h2>
                        </div>
                        <div className={styles.licenseBlock}>
                            <AlertTriangle size={15} strokeWidth={1.75} className={styles.disclaimerIcon} />
                            <div>
                                <p className={styles.disclaimerText}>{t('about.disclaimerBody')}</p>
                            </div>
                        </div>
                        <div className={styles.sectionDivider} />
                        <p className={styles.tosText}>
                            {t('about.disclaimerTos')
                                .split(/\{terms\}|\{privacy\}/)
                                .map((part, i, arr) => (
                                    <span key={i}>
                                        {part}
                                        {i === 0 && i < arr.length - 1 && <Link to="/terms" className={styles.tosLink}>{t('footer.terms')}</Link>}
                                        {i === 1 && i < arr.length - 1 && <Link to="/privacy" className={styles.tosLink}>{t('footer.privacy')}</Link>}
                                    </span>
                                ))}
                        </p>
                    </div>

                    {/* Desktop Promo — web only */}
                    {mode !== 'desktop' && (
                        <div className={styles.desktopPromoCard}>
                            <div className={styles.desktopPromoHeader}>
                                <HardDrive size={20} strokeWidth={1.5} className={styles.desktopPromoIcon} />
                                <div>
                                    <h2 className={styles.desktopPromoTitle}>{t('about.desktopPromoTitle')}</h2>
                                    <p className={styles.desktopPromoBody}>{t('about.desktopPromoBody')}</p>
                                </div>
                            </div>
                            <div className={styles.desktopPromoFooter}>
                                <span className={styles.desktopPromoPrice}>{t('about.desktopPromoPrice')}</span>
                                <Link to="/download">
                                    <Button size="small">{t('about.desktopPromoCta')}</Button>
                                </Link>
                            </div>
                        </div>
                    )}

                    {/* Support — full width, last (web only, free tier only) */}
                    {mode !== 'desktop' && aiTier !== 'pro' && (
                        <div className={styles.supportWrap}>
                            <SupportCard />
                        </div>
                    )}
                </div>
            </div>
        </Shell>
    );
}
